import {
  VantifyProxyResponse,
  vantifyRequest,
} from 'integrations-vantify-api-client';
import { VantifyAuth } from '../common';
import {
  WorkOrderListResponse,
  FetchWorkOrdersParams,
  WorkOrderDetail,
  FetchWorkOrderDetailsParams,
  FetchWorkOrderFilesParams,
  CreateWorkOrderParams,
  AddWorkOrderAttachmentParams,
  DeleteWorkOrderAttachmentParams,
  AcceptWorkOrderApprovalParams,
  AcceptWorkOrderAssignmentParams,
} from './types';
import { VANTIFY_WORK_ORDER_PRIORITY_ID } from '../constants';

export async function getWorkOrders(
  auth: VantifyAuth,
  params: FetchWorkOrdersParams
): Promise<VantifyProxyResponse<WorkOrderListResponse>> {
  if (!params.userId) {
    throw new Error('userId is required to fetch work orders');
  }

  const DEFAULT_LIMIT = 30;
  const { userId, reporter, siteId, page = 1, limit = DEFAULT_LIMIT } = params;

  return await vantifyRequest<WorkOrderListResponse>({
    method: 'GET',
    pathname: `users/${userId}/jobs`,
    query: {
      page,
      limit,
      ...(reporter && { reporter }),
      ...(siteId && { 'sites[]': siteId }),
    },
    auth,
  });
}

/**
 * Get work order details by site ID and job ID
 */
export async function getWorkOrderDetails(
  auth: VantifyAuth,
  params: FetchWorkOrderDetailsParams
): Promise<VantifyProxyResponse<WorkOrderDetail>> {
  const { siteId, jobId } = params;

  if (!siteId) {
    throw new Error('siteId is required to fetch work order details');
  }

  if (!jobId) {
    throw new Error('jobId is required to fetch work order details');
  }

  return await vantifyRequest<WorkOrderDetail>({
    method: 'GET',
    pathname: `sites/${siteId}/jobs/${jobId}`,
    auth,
  });
}

/**
 * Get work order files/attachments by site ID and job ID with pagination and ordering
 */
export async function getWorkOrderFiles(
  auth: VantifyAuth,
  params: FetchWorkOrderFilesParams
): Promise<VantifyProxyResponse> {
  const { siteId, jobId, limit = 30, order = '-id', page = 1 } = params;

  if (!siteId) {
    throw new Error('siteId is required to fetch work order files');
  }

  if (!jobId) {
    throw new Error('jobId is required to fetch work order files');
  }

  return await vantifyRequest({
    method: 'GET',
    pathname: `sites/${siteId}/jobs/${jobId}/files`,
    query: {
      limit,
      order,
      page,
    },
    auth,
  });
}

export async function cancelWorkOrder(
  auth: VantifyAuth,
  siteId: number,
  jobId: number,
  note: string
): Promise<VantifyProxyResponse> {
  if (!siteId) {
    throw new Error('siteId is required to cancel work order');
  }

  if (!jobId) {
    throw new Error('jobId is required to cancel work order');
  }

  if (!note) {
    throw new Error('note is required to cancel work order');
  }

  return await vantifyRequest({
    method: 'POST',
    pathname: `sites/${siteId}/jobs/${jobId}/workflow/operation/cancel`,
    body: {
      note,
    },
    auth,
  });
}

/**
 * Create a reactive helpdesk work order
 */
export async function createWorkOrder(
  auth: VantifyAuth,
  params: CreateWorkOrderParams
): Promise<VantifyProxyResponse> {
  const {
    siteId,
    jobGroupId,
    reporter,
    siteContact,
    summary,
    description,
    files,
  } = params;

  if (!siteId) {
    throw new Error('siteId is required to create work order');
  }

  if (!jobGroupId) {
    throw new Error('jobGroupId is required to create work order');
  }

  if (!reporter?.name || !reporter?.email) {
    throw new Error(
      'reporter name and email are required to create work order'
    );
  }

  if (!siteContact?.name || !siteContact?.email) {
    throw new Error(
      'siteContact name and email are required to create work order'
    );
  }

  if (!summary) {
    throw new Error('summary is required to create work order');
  }

  const body = {
    _links: {
      jobgroup: {
        href: `/jobgroups/${jobGroupId}`,
      },
      priority: {
        href: `/priorities/${VANTIFY_WORK_ORDER_PRIORITY_ID}`,
      },
    },
    reporter: {
      name: reporter.name,
      email: reporter.email,
    },
    reporterTenant: {
      notifyOnCreate: false,
      notifyOnComplete: false,
    },
    siteContact: {
      name: siteContact.name,
      email: siteContact.email,
      notifyOnCreate: true,
      notifyOnComplete: true,
    },
    contactTenant: {
      notifyOnCreate: false,
      notifyOnComplete: false,
    },
    siteContactAvailableOnSite: true,
    skipRequestApprovalNotification: false,
    isOnNextPlannedVisit: false,
    summary,
    ...(description && { description }),
    noRequisiteRequired: true,
    ...(files && { files }),
  };

  return await vantifyRequest({
    method: 'POST',
    pathname: `sites/${siteId}/jobs/reactive/helpdesk`,
    body,
    auth,
  });
}

/**
 * Add an attachment/file to a work order
 */
export async function addWorkOrderAttachment(
  auth: VantifyAuth,
  params: AddWorkOrderAttachmentParams
): Promise<VantifyProxyResponse> {
  const { siteId, jobId, fileHref, title } = params;

  if (!siteId) {
    throw new Error('siteId is required to add work order attachment');
  }

  if (!jobId) {
    throw new Error('jobId is required to add work order attachment');
  }

  if (!fileHref) {
    throw new Error('fileHref is required to add work order attachment');
  }

  if (!title) {
    throw new Error('title is required to add work order attachment');
  }

  const body = {
    _links: {
      file: {
        href: fileHref,
      },
    },
    title,
  };

  return await vantifyRequest({
    method: 'POST',
    pathname: `sites/${siteId}/jobs/${jobId}/files`,
    body,
    auth,
  });
}

export async function deleteWorkOrderAttachment(
  auth: VantifyAuth,
  params: DeleteWorkOrderAttachmentParams
): Promise<VantifyProxyResponse> {
  const { fileHref } = params;

  if (!fileHref) {
    throw new Error('fileHref is required to delete work order attachment');
  }

  return await vantifyRequest({
    method: 'DELETE',
    pathname: `files/${fileHref}`,
    auth,
  });
}

/**
 * Accept work order approval
 */
export async function acceptWorkOrderApproval(
  auth: VantifyAuth,
  params: AcceptWorkOrderApprovalParams
): Promise<VantifyProxyResponse> {
  const { siteId, jobId, userId, serviceProviderId, jobGroupId, description, summary } = params;

  if (!jobGroupId) {
    throw new Error('jobGroupId is required to accept work order approval');
  }

  if (!siteId) {
    throw new Error('siteId is required to accept work order approval');
  }

  if (!jobId) {
    throw new Error('jobId is required to accept work order approval');
  }

  if (!userId) {
    throw new Error('userId is required to accept work order approval');
  }

  if (!serviceProviderId) {
    throw new Error('serviceProviderId is required to accept work order approval');
  }

  if (!summary) {
    throw new Error('summary is required to accept work order approval');
  }

  const body = {
    _links: {
      jobgroup: {
        href: `/jobgroups/${jobGroupId}`,
      },
      serviceprovider: {
        href: `/serviceproviders/${serviceProviderId}`,
      },
      priority: {
        href: `/priorities/${VANTIFY_WORK_ORDER_PRIORITY_ID}`,
      },
      approver: {
        href: `/users/${userId}`,
      },
    },
    summary,
    ...(description && { description }),
  };

  return await vantifyRequest({
    method: 'POST',
    pathname: `sites/${siteId}/jobs/${jobId}/workflow/approval/accept`,
    body,
    auth,
  });
}

/**
 * Accept work order assignment
 */
export async function acceptWorkOrderAssignment(
  auth: VantifyAuth,
  params: AcceptWorkOrderAssignmentParams
): Promise<VantifyProxyResponse> {
  const { siteId, jobId, note } = params;

  if (!siteId) {
    throw new Error('siteId is required to accept work order assignment');
  }

  if (!jobId) {
    throw new Error('jobId is required to accept work order assignment');
  }

  const body: { note?: string } = {};

  if (note) {
    body.note = note;
  }

  return await vantifyRequest({
    method: 'POST',
    pathname: `sites/${siteId}/jobs/${jobId}/workflow/assignment/accept`,
    body,
    auth,
  });
}
